import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  createOrder,
  fetchAllOrders,
  fetchOrder,
  cancelOrder
} from "./orderAPI";
import { unwrapApiResponse } from "../../util/util";
import { getCart } from "../products/productSlice";

const initialState = {
  allOrders: [],
  status: "idle",
  selectedOrder: {},
  currentOrder: {},
  createOrderStatus: "idle",
  orderFetchStatus: "idle",
  orderCancelStatus: "idle"
};

/**
 * Saves an order to the server.
 * @param {object} allOrders - The order object to save.
 * @return {Promise<void>} A promise that resolves when
 * the order has been saved.
 */
export const getAllOrders = createAsyncThunk(
  "order/fetchAllOrders",
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetchAllOrders();
      return unwrapApiResponse(response, rejectWithValue);
    } catch (error) {
      return rejectWithValue(error.message || "Failed to get all orders");
    }
  }
);

/**
 * Saves an order to the server.
 * @param {object} selectedOrder - The order object to save.
 * @return {Promise<void>} A promise that resolves when the
 * order has been saved.
 */
export const getSelectedOrder = createAsyncThunk(
  "order/fetchOrder",
  async (orderId) => {
    const response = await fetchOrder(orderId);
    return unwrapApiResponse(response, rejectWithValue);
  }
);

/**
 * Saves an order to the server.
 * @param {object} currentOrder - The order object to save.
 * @return {Promise<void>} A promise that resolves when the
 * order has been saved.
 */
export const placeOrder = createAsyncThunk(
  "order/placeOrder",
  async ({ address }, { dispatch, rejectWithValue }) => {
    try {
      const response = await createOrder({
        addressId: address.id,
        paymentMethodId: 1
      });
      const data = unwrapApiResponse(response, rejectWithValue);
      dispatch(getCart());
      return data;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to place order");
    }
  }
);

/**
 * Saves an order to the server.
 * @param {object} orderId - The order object to save.
 * @return {Promise<void>} A promise that resolves when the
 * order has been saved.
 */
export const cancelCustomerOrder = createAsyncThunk(
  "order/cancelCustomerOrder",
  async ({ orderId }) => {
    const response = await cancelOrder(orderId);
    return unwrapApiResponse(response, rejectWithValue);
  }
);

export const orderSlice = createSlice({
  name: "order",
  initialState,
  // The `reducers` field lets us define reducers
  // and generate associated actions
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getAllOrders.pending, (state) => {
        state.orderFetchStatus = "pending";
      })
      .addCase(getAllOrders.fulfilled, (state, action) => {
        state.orderFetchStatus = "fulfilled";
        state.allOrders = action.payload;
      })
      .addCase(getSelectedOrder.pending, (state) => {
        state.status = "pending";
      })
      .addCase(getSelectedOrder.fulfilled, (state, action) => {
        state.status = "fulfilled";
        state.selectedOrder = action.payload;
      })
      .addCase(placeOrder.pending, (state) => {
        state.createOrderStatus = "pending";
      })
      .addCase(placeOrder.fulfilled, (state, action) => {
        state.createOrderStatus = "idle";
        state.currentOrder = action.payload;
      })
      .addCase(placeOrder.rejected, (state, action) => {
        state.createOrderStatus = "idle";
        state.error = action.payload || action.error.message;
      })
      .addCase(cancelCustomerOrder.pending, (state) => {
        state.orderCancelStatus = "pending";
      })
      .addCase(cancelCustomerOrder.fulfilled, (state, action) => {
        state.orderCancelStatus = action.payload;
      });
  }
});

export const allOrders = (state) => state.order.allOrders;
export const selectedOrder = (state) => state.order.selectedOrder;
export const currentOrder = (state) => state.order.currentOrder;
export const createOrderStatus = (state) => state.order.createOrderStatus;
export const orderFetchStatus = (state) => state.order.orderFetchStatus;
export const orderCancelStatus = (state) => state.order.orderCancelStatus;

export default orderSlice.reducer;
